
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { Position } from "@/types/locationTypes";
import { 
  ShotHistoryRecord, 
  fetchShotHistory, 
  saveShotToHistory, 
  deleteShotFromHistory 
} from "@/utils/historyUtils";

/**
 * Hook for managing golf shot history
 */
export const useShotHistory = () => {
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const [history, setHistory] = useState<ShotHistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch history on component mount
  useEffect(() => {
    loadHistory();
  }, []);
  
  // Load shot history from the database
  const loadHistory = useCallback(async (limit?: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await fetchShotHistory(limit);
      
      if (error) {
        setError(error);
        toast({
          title: t('toasts.errorSaving.title'),
          description: `${t('ballDistance.history.loading')}: ${error}`,
          variant: "destructive",
        });
      } else if (data) {
        setHistory(data);
      }
    } catch (err: any) {
      setError(err.message);
      toast({
        title: t('toasts.errorSaving.title'),
        description: `${t('ballDistance.history.loading')}: ${err.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  // Save a new shot to history
  const saveShot = useCallback(async (
    startPosition: Position,
    endPosition: Position,
    distance: number,
    tendency?: 'left' | 'right' | null,
    club?: string,
    notes?: string
  ) => {
    setError(null);
    
    try {
      const { success, error } = await saveShotToHistory(
        startPosition,
        endPosition,
        distance,
        tendency,
        club,
        notes
      );
      
      if (!success) {
        setError(error || t('toasts.errorSaving.description'));
        toast({
          title: t('toasts.errorSaving.title'),
          description: `${t('ballDistance.dialogs.saveShot.confirm')}: ${error}`,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: t('toasts.resultSaved.title'),
        description: t('toasts.resultSaved.description'),
      });
      
      // Reload history to show the new entry
      await loadHistory();
      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: t('toasts.errorSaving.title'),
        description: `${t('toasts.errorSaving.description')}: ${err.message}`,
        variant: "destructive",
      });
      return false;
    }
  }, [toast, loadHistory]);
  
  // Delete a shot from history
  const deleteShot = useCallback(async (id: string) => {
    setError(null);
    
    try {
      const { success, error } = await deleteShotFromHistory(id);
      
      if (!success) {
        setError(error || t('toasts.errorDeleting.description'));
        toast({
          title: t('toasts.errorDeleting.title'), 
          description: `${t('ballDistance.history.delete')}: ${error}`,
          variant: "destructive",
        });
        return false;
      }
      
      // Update local state by removing the deleted entry
      setHistory(prevHistory => prevHistory.filter(shot => shot.id !== id));
      
      toast({
        title: t('toasts.dataDeleted.title'),
        description: t('toasts.dataDeleted.description'),
      });
      
      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: t('toasts.errorDeleting.title'),
        description: `${t('toasts.errorDeleting.description')}: ${err.message}`,
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);
  
  return {
    history,
    isLoading,
    error,
    loadHistory,
    saveShot,
    deleteShot
  };
};
